import type { BilletterieOrder } from '@cpce/shared';
import type { TicketingCustomerOrder } from '@cpce/ticketing-api';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import Decimal from 'decimal.js';
import { Badge, BadgeColor } from '@/components/Badge';
import {
  CUSTOMER_PAYMENT_MODES,
  describePaymentMethods,
} from '@/features/billetterie-orders/billetterie-orders.utils';
import { AttachOrderCell } from '@/features/billetterie-orders/components/AttachOrderCell';
import { formatDate } from '@/utils/date.utils';
import { formatDecimalAsPrice } from '@/utils/string.utils';

const columnHelper = createColumnHelper<BilletterieOrder>();

const formatCents = (cents: number) => formatDecimalAsPrice(new Decimal(cents).div(100));

const summarizeItems = (order: TicketingCustomerOrder) =>
  order.items
    .map((item) => {
      const name = item.productName ?? 'Produit';
      // The billetterie sometimes returns `variant` as an IRI rather than a label — only show it
      // when it's a clean human-readable value.
      const variant = item.variant && !item.variant.includes('/') ? ` (${item.variant})` : '';
      return `${name}${variant} ×${item.quantity}`;
    })
    .join(', ');

const totalQuantity = (order: TicketingCustomerOrder) =>
  order.items.reduce((sum, item) => sum + item.quantity, 0);

// The billetterie's state/paymentState vocabulary isn't fully fixed; map the common values and
// fall back to showing the raw pair so nothing is silently hidden.
function orderStatusDisplay(order: TicketingCustomerOrder): { label: string; color: BadgeColor } {
  if (order.state === 'cancelled' || order.paymentState === 'cancelled') {
    return { label: 'Annulée', color: BadgeColor.GRAY };
  }
  if (order.paymentState === 'refunded') return { label: 'Remboursée', color: BadgeColor.GRAY };
  if (order.paymentState === 'paid') return { label: 'Payée', color: BadgeColor.GREEN };
  if (order.paymentState === 'awaiting_payment') {
    return { label: 'En attente de paiement', color: BadgeColor.ORANGE };
  }
  return { label: `${order.state} / ${order.paymentState}`, color: BadgeColor.BLUE };
}

export const paymentColumns = [
  columnHelper.accessor(describePaymentMethods, {
    id: 'paymentMethods',
    header: 'Paiement',
    cell: (info) =>
      info.row.original.isDeferredPayment ? (
        <Badge color={BadgeColor.PURPLE}>{info.getValue()}</Badge>
      ) : (
        info.getValue()
      ),
    meta: { filterType: 'text' },
  }),
  columnHelper.accessor(
    (row) =>
      row.customerPaymentMode ? CUSTOMER_PAYMENT_MODES[row.customerPaymentMode].label : '-',
    {
      id: 'customerPaymentMode',
      header: 'Réglé au CSE en',
      meta: { filterType: 'text' },
    }
  ),
] as ColumnDef<BilletterieOrder>[];

// Built once per salarié: a new column set on every render remounts the table's cells.
export const createAttachColumn = (cardId: number | null) =>
  columnHelper.display({
    id: 'attach',
    header: 'Comptabilité CSE',
    cell: (info) => <AttachOrderCell order={info.row.original} cardId={cardId} />,
  }) as ColumnDef<BilletterieOrder>;

export const ordersColumns = [
  columnHelper.accessor((row) => row.checkoutCompletedAt, {
    id: 'date',
    header: 'Date',
    cell: (info) => {
      const value = info.getValue();
      return value ? formatDate(new Date(value)) : '-';
    },
    meta: { filterType: 'numeric' },
  }),
  columnHelper.accessor((row) => row.number ?? '-', {
    id: 'number',
    header: 'N° commande',
    meta: { filterType: 'text' },
  }),
  columnHelper.accessor(summarizeItems, {
    id: 'products',
    header: 'Produit(s)',
    meta: { filterType: 'text' },
  }),
  columnHelper.accessor(totalQuantity, {
    id: 'quantity',
    header: 'Quantité',
    meta: { filterType: 'numeric' },
  }),
  columnHelper.accessor((row) => row.total, {
    id: 'total',
    header: 'Montant',
    cell: (info) => formatCents(info.getValue()),
    meta: { filterType: 'numeric' },
  }),
  columnHelper.accessor((row) => row.subventionAmount, {
    id: 'subvention',
    header: 'Part CE',
    cell: (info) => formatCents(info.getValue()),
    meta: { filterType: 'numeric' },
  }),
  columnHelper.accessor((row) => orderStatusDisplay(row).label, {
    id: 'status',
    header: 'Statut',
    cell: (info) => {
      const display = orderStatusDisplay(info.row.original);
      return <Badge color={display.color}>{display.label}</Badge>;
    },
    meta: { filterType: 'text' },
  }),
] as ColumnDef<BilletterieOrder>[];
